import { z } from 'zod';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { RootCauseAddingVariablesGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseBinScatterControlledVsUncontrolledGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';

export const BinScatterGroupResult = z.object({
  trendline: z.object({
    Y0: z.number(),
    Y1: z.number(),
  }),
  coordinates: z.array(
    z.object({
      X: z.number(),
      Y: z.number(),
    }),
  ),
});

export type BinScatterGroupResult = z.infer<typeof BinScatterGroupResult>;

export const BinScatterDemographicTakeaway = z.object({
  // These are strings instead of numbers because the Python script may return a string such
  // as "Not enough data" to signify that the result is not valid.
  // It is up to the frontend to parse these strings into numbers when possible, and assume any non-parseable
  // strings are not valid and therefore a warning of "not enough data" should be shown.
  disparity: z.string().optional(),
  p_value: z.string().optional(),
});

export type BinScatterDemographicTakeaway = z.infer<
  typeof BinScatterDemographicTakeaway
>;

const BinScatterDemographicTakeaways = z.record(
  z.string(),
  BinScatterDemographicTakeaway,
);

export type BinScatterDemographicTakeaways = z.infer<
  typeof BinScatterDemographicTakeaways
>;

export const BinScatterKeyTakeaways = z.record(
  z.string(),
  BinScatterDemographicTakeaways,
);

export type BinScatterKeyTakeaways = z.infer<typeof BinScatterKeyTakeaways>;

export const BinScatterMultiSelectResult = z.object({
  results: z.record(z.string(), BinScatterGroupResult),
  key_takeaways: BinScatterKeyTakeaways.optional(),
});

export type BinScatterMultiSelectResult = z.infer<
  typeof BinScatterMultiSelectResult
>;

export const BinScatterCrossSelectResult = z.object({
  Group1: BinScatterGroupResult,
  Group2: BinScatterGroupResult,
});

export type BinScatterCrossSelectResult = z.infer<
  typeof BinScatterCrossSelectResult
>;

export const BinScatterChartMultiSelectProps = z.object({
  binScatterMultiSelectOutput: BinScatterMultiSelectResult,
  focusArea: MyFocusAreaDefinition,
  metricDefinition: MyMetricDefinition,
  dimension: z.string(),
  activeLevelKeys: z.array(z.string()),
});

export type BinScatterChartMultiSelectProps = z.infer<
  typeof BinScatterChartMultiSelectProps
>;

export const BinScatterChartCrossSelectProps = z.object({
  binScatterCrossSelectOutput: BinScatterCrossSelectResult,
  dimension: z.string(),
  focusArea: MyFocusAreaDefinition,
  metricDefinition: MyMetricDefinition,
});

export type BinScatterChartCrossSelectProps = z.infer<
  typeof BinScatterChartCrossSelectProps
>;

export const BinScatterControlledVsUncontrolledProps = z.object({
  groupResult: z.union([
    RootCauseBinScatterControlledVsUncontrolledGroupResult,
    RootCauseAddingVariablesGraphGroupResult,
  ]),
  dimension: z.string(),
  focusArea: MyFocusAreaDefinition,
  metricDefinition: MyMetricDefinition,
  overridingYAxisText: z.string().nullable(),
  overridingXAxisText: z.string().nullable(),
});

export type BinScatterControlledVsUncontrolledProps = z.infer<
  typeof BinScatterControlledVsUncontrolledProps
>;

export const BinScatterChartProps = z.union([
  BinScatterChartMultiSelectProps,
  BinScatterChartCrossSelectProps,
  BinScatterControlledVsUncontrolledProps,
]);

export type BinScatterChartProps = z.infer<typeof BinScatterChartProps>;

export const BinScatterChartSerializableProps = z.intersection(
  BinScatterChartProps,
  z.object({
    focusedLine: z.string().nullable(),
    allPrivilegedLevels: z.array(LevelDefinition),
    levelsForDimension: z.array(LevelDefinition),
    levelsByColumnValue: z.record(z.string(), LevelDefinition),
  }),
);

export type BinScatterChartSerializableProps = z.infer<
  typeof BinScatterChartSerializableProps
>;
